import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pathname } from 'Routes';
import {
  createRoom, getRoomSignalingUrl, closeRoom, getIceServers,
} from 'Api/roomsApi';
import {
  overlay, center, card, heading, body,
  primaryBtn, secondaryBtn, ctaRow, videoFrame, videoTag, isMobileQuery,
} from './styles';
import { InterviewCall } from './InterviewCall';

// Host-side setup screen for the interview-record feature.
//
// Lifecycle (state machine):
//   1. acquiring   — asking the browser for camera + mic
//   2. ready       — preview live, room created, link shown, WS open
//                    waiting for the guest. Host can also hit Cancel.
//   3. guest-here  — guest joined via WS (peer-joined message). UI flips
//                    to "Start call" CTA. (PR 3 will navigate into the
//                    actual call UI; for now we just show readiness.)
//   4. error       — permission denied, room create failed, etc.
//
// The signaling WS is opened lazily AFTER the room is created. We close
// it (and the room) on unmount so a backed-out host doesn't leave dangling
// rooms / sockets.
export const InterviewHost = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = (location.state && location.state.nextPath) || Pathname.profile.basePath;

  const [isMobile, setIsMobile] = useState(isMobileQuery);
  useEffect(() => {
    const onResize = () => setIsMobile(isMobileQuery());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [phase, setPhase] = useState('acquiring');
  const [error, setError] = useState('');
  const [room, setRoom] = useState(null); // { id, expires_at, ice_servers }
  const [guestArrived, setGuestArrived] = useState(false);
  const [copyState, setCopyState] = useState('idle'); // idle | copied
  const [ws, setWs] = useState(null);
  const [iceServers, setIceServers] = useState([]);
  const [stream, setStream] = useState(null);
  const [inCall, setInCall] = useState(false);
  // Bumped every time the guest arrives via WS. The InterviewCall is
  // mounted with `key={`call-${negotiationKey}`}` so each peer-joined
  // forces a full unmount → mount, which throws away any stale
  // RTCPeerConnection from a previous (now-disconnected) session and
  // sends a fresh offer to the freshly-reconnected guest. Without this,
  // a guest who closes their tab and rejoins gets stuck on the host's
  // dead PC forever.
  const [negotiationKey, setNegotiationKey] = useState(0);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);

  // Cleanup hook — used on unmount, on cancel, and on hard errors so we
  // don't leak camera tracks or leave a half-open WebSocket / room.
  const teardown = useCallback(async () => {
    try { if (wsRef.current) wsRef.current.close(); } catch (e) { /* noop */ }
    wsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (e) { /* noop */ }
      });
      streamRef.current = null;
    }
    if (room && room.id) {
      // Best-effort: server cleanup also expires the room after 24h, but
      // closing it now frees the host slot immediately if the user comes
      // back through the intro page.
      try { await closeRoom(room.id); } catch (e) { /* noop */ }
    }
  }, [room]);

  // ── Bootstrap: ask for media + create the room in parallel ──
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      // 1) Camera + mic. iOS Safari requires HTTPS + user-gesture, which
      //    we already have (the user clicked "Yes" on the intro).
      try {
        const acquired = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        if (cancelled) {
          acquired.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = acquired;
        setStream(acquired);
        if (videoRef.current) {
          videoRef.current.srcObject = acquired;
        }
      } catch (e) {
        if (!cancelled) {
          setError('We need access to your camera and microphone to record.');
          setPhase('error');
        }
        return;
      }

      // 2) Create the room + fetch ICE servers in parallel.
      let created;
      let ice;
      try {
        [created, ice] = await Promise.all([
          createRoom({ source: 'fe.interviewHost' }),
          getIceServers(),
        ]);
      } catch (e) {
        if (!cancelled) {
          setError('Could not create the meeting room. Please try again.');
          setPhase('error');
        }
        return;
      }
      if (cancelled) return;
      setRoom(created);
      // The /ice-servers/ endpoint returns { iceServers: [...] }; pass
      // the inner array straight to RTCPeerConnection.
      setIceServers((ice && ice.iceServers) || (created.ice_servers) || []);

      // 3) Open the signaling socket as host. We don't do any WebRTC
      //    work here yet — PR 3 owns the peer connection. For now we
      //    just listen for `peer-joined` so the UI can flip when the
      //    guest arrives.
      const sock = new WebSocket(getRoomSignalingUrl(created.id, 'host'));
      wsRef.current = sock;
      sock.onopen = () => {
        if (cancelled) return;
        setWs(sock); // publish to state so InterviewCall can attach
        setPhase('ready');
      };
      sock.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg && msg.type === 'peer-joined' && msg.role === 'guest') {
            setGuestArrived(true);
            // Force a fresh InterviewCall mount on every guest-join so
            // reconnections (guest closed tab + rejoined) get a brand
            // new RTCPeerConnection + offer dance instead of the host
            // sitting on a dead PC.
            setNegotiationKey((k) => k + 1);
          }
          if (msg && msg.type === 'peer-left' && msg.role === 'guest') {
            setGuestArrived(false);
          }
        } catch (e) { /* ignore unparseable messages */ }
      };
      sock.onerror = () => { /* see PR 4 for reconnect strategy */ };
      sock.onclose = () => {
        wsRef.current = null;
        setWs(null);
      };
    };

    bootstrap();
    return () => {
      cancelled = true;
      teardown();
    };
    // bootstrap-once on mount; the teardown closure captures the room via
    // setRoom-time and pulls a fresh wsRef/streamRef on cleanup.
  }, []);

  const guestLink = room
    ? `${window.location.origin}${Pathname.getInterviewJoinPath(room.id)}`
    : '';

  const handleCopy = async () => {
    if (!guestLink) return;
    try {
      await navigator.clipboard.writeText(guestLink);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch (e) {
      // Fallback for older Safari / iOS — select+execCommand. Keeping
      // this path because the gift-share modal uses the same trick.
      const ta = document.createElement('textarea');
      ta.value = guestLink;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopyState('copied'); } catch (err) { /* noop */ }
      document.body.removeChild(ta);
      setTimeout(() => setCopyState('idle'), 1800);
    }
  };

  const handleCancel = () => {
    teardown();
    navigate(nextPath, { replace: true });
  };

  // Guest is here + WS is open + camera is live → flip to in-call mode.
  // We keep the host page mounted (so the WS doesn't churn) and render
  // <InterviewCall> inside it; the setup UI is replaced. Auto-transition
  // is intentional — once everything is ready, there's no value in a
  // manual "start call" tap. The explicit action lives inside the call
  // view as "Start Recording".
  useEffect(() => {
    if (!inCall && guestArrived && ws && stream && phase === 'ready') {
      setInCall(true);
    }
  }, [inCall, guestArrived, ws, stream, phase]);

  // Send a 'bye' over the WS BEFORE we tear it down. This is how the
  // guest distinguishes an intentional host exit ("Session ended —
  // thanks!") from an unexpected drop ("Host disconnected"). Without
  // this, both look identical to the guest (just peer-left), and the
  // guest can't show the right end-state copy.
  const sendByeAndTeardown = () => {
    const sock = wsRef.current;
    if (sock && sock.readyState === WebSocket.OPEN) {
      try { sock.send(JSON.stringify({ type: 'bye', payload: {} })); } catch (e) { /* noop */ }
    }
    teardown();
  };

  // Host's recording is finalized → hand it off to the review page.
  // The recorded File travels through history.state (structured-cloned),
  // and the review page picks it up via location.state.file. We also
  // pass nextPath so the review's "use it" handler can route the user
  // back into onboarding once the upload kicks off.
  const handleRecordingFinalized = (file) => {
    sendByeAndTeardown();
    navigate(Pathname.interview.review, {
      state: { file, nextPath },
      replace: true,
    });
  };

  const handleLeaveCall = () => {
    sendByeAndTeardown();
    navigate(nextPath, { replace: true });
  };

  // ── In-call view — InterviewCall is rendered at the document root
  // (no card wrapper) so it can claim the full viewport and never
  // scrolls. The `key` prop forces a fresh mount whenever the guest
  // reconnects, see negotiationKey above. ──
  if (inCall) {
    return (
      <InterviewCall
        key={`call-${negotiationKey}`}
        peerRole="host"
        localStream={stream}
        ws={ws}
        iceServers={iceServers}
        onLeave={handleLeaveCall}
        onRecordingFinalized={handleRecordingFinalized}
      />
    );
  }

  return (
    <div style={overlay}>
      <div style={center(isMobile)}>
        <div style={card(isMobile)}>
          <h1 style={heading(isMobile)}>Get ready to record</h1>
          <p style={body}>
            Share the link below. The moment they join, we&apos;ll let you start recording.
          </p>

          <div style={videoFrame}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={videoTag}
            />
            {phase === 'acquiring' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#cfd1da',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                Asking for camera + mic…
              </div>
            )}
            {phase === 'error' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#ff9b9b',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textAlign: 'center',
                  padding: '20px',
                  gap: '10px',
                }}
              >
                <span>{error}</span>
                <button type="button" onClick={() => window.location.reload()} style={secondaryBtn}>
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* Invite link + copy. Shown only once the room is created so we
              never display an empty / stale URL. */}
          {room && (
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '8px',
                alignItems: 'stretch',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '12px',
                padding: '10px',
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '10px 12px',
                  borderRadius: '8px',
                  background: 'rgba(0,0,0,0.25)',
                  color: '#e6e8ee',
                  fontFamily: "'Menlo', 'Consolas', monospace",
                  fontSize: '0.85rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={guestLink}
              >
                {guestLink}
              </div>
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  ...primaryBtn(false),
                  padding: '10px 18px',
                  minHeight: '44px',
                  fontSize: '0.9rem',
                }}
              >
                {copyState === 'copied' ? 'Copied ✓' : 'Copy link'}
              </button>
            </div>
          )}

          {/* Status line. The link copy + a clear "what's happening" pill
              is more reassuring than a spinner. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              color: guestArrived ? '#bce8c2' : '#cfd1da',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '999px',
                background: guestArrived ? '#5ad77a' : '#ffd166',
                boxShadow: guestArrived
                  ? '0 0 0 6px rgba(90, 215, 122, 0.18)'
                  : '0 0 0 6px rgba(255, 209, 102, 0.18)',
              }}
            />
            {guestArrived ? 'Your guest is here' : 'Waiting for your guest to join…'}
          </div>

          <div style={ctaRow}>
            <button type="button" onClick={handleCancel} style={secondaryBtn}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewHost;
